import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, getStatusBadgeStyle, formatDate, formatCurrency, calcHours, fmtHours } from '../styles';
import { fetchLabourRates, calcDayworkSheetCost } from '../labourRates';

// ---- Project List Page ----
export function ProjectListPage({ navigate }) {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const role = profile?.role || '';
  const isAdmin = role === 'admin';

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // 8-second loading safety net
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function loadProjects() {
    setLoading(true);
    try {
      if (isAdmin) {
        // Admin sees all projects
        const { data, error: e } = await supabase
          .from('projects')
          .select('*')
          .order('created_at', { ascending: false });
        if (e) throw e;
        setProjects(data || []);
      } else {
        // Others see their assigned projects
        const { data, error: e } = await supabase
          .from('project_users')
          .select('project_id, projects(*)')
          .eq('user_id', profile.id);
        if (e) throw e;
        setProjects((data || []).map(pu => pu.projects).filter(Boolean));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.pageContainer}>
      <div style={S.pageHeader}>
        <h1 style={S.pageTitle}>Projects</h1>
        {isAdmin && (
          <button
            onClick={() => navigate('admin', { tab: 'projects' })}
            style={{ ...S.btn, ...S.btnPrimary }}
          >
            + New Project
          </button>
        )}
      </div>

      {error && <div style={S.alertError}>{error}</div>}
      {loading ? (
        <div style={S.spinner}>Loading projects...</div>
      ) : projects.length === 0 ? (
        <div style={S.emptyState}>
          <div style={S.emptyStateIcon}>📁</div>
          <div style={S.emptyStateText}>No projects found</div>
          <div style={S.emptyStateSubtext}>
            {isAdmin ? 'Create your first project in the Admin panel.' : 'You have not been assigned to any projects yet.'}
          </div>
        </div>
      ) : (
        <div style={S.grid2}>
          {projects.map(project => (
            <ProjectCard key={project.id} project={project} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project, navigate }) {
  return (
    <div
      onClick={() => navigate('project-detail', { projectId: project.id })}
      style={{
        ...S.card,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.1s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'none';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <span style={{ fontWeight: '700', fontSize: '12px', color: C.accent, letterSpacing: '0.05em' }}>
          {project.reference}
        </span>
        <span style={{
          ...S.badge,
          ...(project.active
            ? { background: C.successLight, color: C.success }
            : { background: '#f3f4f6', color: C.textMuted }),
        }}>
          {project.active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <h3 style={{ fontSize: '16px', fontWeight: '600', color: C.text, margin: '0 0 8px 0' }}>
        {project.name}
      </h3>
      {project.main_contractor && (
        <div style={{ fontSize: '13px', color: C.textMuted, marginBottom: '4px' }}>
          <span style={{ fontWeight: '500' }}>MC:</span> {project.main_contractor}
        </div>
      )}
      {project.contract_value && (
        <div style={{ fontSize: '13px', color: C.textMuted, marginBottom: '4px' }}>
          <span style={{ fontWeight: '500' }}>Value:</span> {formatCurrency(project.contract_value)}
        </div>
      )}
      {project.address && (
        <div style={{ fontSize: '12px', color: C.textLight, marginTop: '8px' }}>
          {project.address}
        </div>
      )}
    </div>
  );
}

// ---- Project Detail Page (Pipeline View) ----

const INSTR_TYPE_BADGE = {
  EWN: { background: '#fef3c7', color: '#92400e' },
  CVI: { background: '#dbeafe', color: '#1e40af' },
  IR:  { background: '#0f172a', color: '#e2e8f0' },
};
const NODE_STYLE = {
  complete:   { bg: '#EAF3DE', border: '1px solid #97C459',  color: '#27500A', sub: '#3B6D11' },
  inprogress: { bg: '#FAEEDA', border: '1px solid #EF9F27',  color: '#633806', sub: '#854F0B' },
  disputed:   { bg: '#FCEBEB', border: '1px solid #F09595',  color: '#791F1F', sub: '#A32D2D' },
  draft:      { bg: '#f3f4f6', border: '1px solid #d1d5db',  color: '#374151', sub: '#6b7280' },
  empty:      { bg: 'transparent', border: '1px dashed #d1d5db', color: '#9ca3af', sub: '#9ca3af' },
};
const ROW_STATUS = {
  complete:   { bg: '#f0fdf4', borderColor: '#16a34a' },
  inprogress: { bg: '#ffffff', borderColor: '#f59e0b' },
  disputed:   { bg: '#fef2f2', borderColor: '#dc2626' },
  draft:      { bg: '#f9fafb', borderColor: '#e5e7eb' },
};
const DW_PILL = {
  draft:     { background: '#f3f4f6', color: '#374151' },
  submitted: { background: '#dbeafe', color: '#1e40af' },
  signed:    { background: '#d1fae5', color: '#065f46' },
  disputed:  { background: '#fee2e2', color: '#991b1b' },
};
const VAR_PILL = {
  draft:       { background: '#f3f4f6', color: '#374151' },
  qs_review:   { background: '#fef3c7', color: '#92400e' },
  submitted:   { background: '#dbeafe', color: '#1e40af' },
  priced:      { background: '#ede9fe', color: '#5b21b6' },
  agreed:      { background: '#d1fae5', color: '#065f46' },
  part_agreed: { background: '#fef3c7', color: '#92400e' },
  disputed:    { background: '#fee2e2', color: '#991b1b' },
};

function PipelineNode({ nodeData, onClick, children }) {
  const ns = NODE_STYLE[nodeData.state] || NODE_STYLE.empty;
  return (
    <div
      onClick={onClick}
      style={{ background: ns.bg, border: ns.border, borderRadius: '6px', padding: '8px 10px',
        cursor: onClick ? 'pointer' : 'default', height: '100%', minHeight: '52px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
    >
      <div style={{ fontSize: '12px', fontWeight: '600', color: ns.color, lineHeight: '1.3' }}>{nodeData.label}</div>
      {nodeData.sub && <div style={{ fontSize: '11px', color: ns.sub, marginTop: '2px', lineHeight: '1.3' }}>{nodeData.sub}</div>}
      {children}
    </div>
  );
}

function getRowStatus(ins, irByParent) {
  const linkedIr = irByParent[ins.id];
  const dws = (linkedIr || ins).dayworks || [];
  const v = ((linkedIr || ins).variations || [])[0] || null;
  if (dws.some(d => d.status === 'disputed') || v?.status === 'disputed' || ins.mc_response === 'disputed') return 'disputed';
  if ((dws.length > 0 && dws.every(d => d.status === 'signed')) || v?.status === 'agreed' || v?.status === 'part_agreed') return 'complete';
  if (dws.some(d => d.status !== 'draft') || v || ins.sent_at || ins.mc_response) return 'inprogress';
  return 'draft';
}

const PIPELINE_GRID = '200px 2fr 2fr 1.75fr 1.75fr 1.25fr 28px';
const PIPELINE_COLS = ['Instruction', 'IR / Instruction Link', 'Valued As', 'Submitted to MC', 'MC Response', 'On Application'];

function renderPipelineRow({ ins, irByParent, instrById, navigate, projectId }) {
  const isEwnCvi = ins.type === 'EWN' || ins.type === 'CVI';
  const linkedIr = irByParent[ins.id];
  const typeBadge = INSTR_TYPE_BADGE[ins.type] || { background: '#f3f4f6', color: '#374151' };

  const effectiveDws = isEwnCvi ? (linkedIr?.dayworks || []) : (ins.dayworks || []);
  const effectiveVar = isEwnCvi ? ((linkedIr?.variations || [])[0] || null) : ((ins.variations || [])[0] || null);
  const effectiveInstrId = isEwnCvi ? (linkedIr?.id || null) : ins.id;
  const navTarget = (isEwnCvi && linkedIr) ? linkedIr.id : ins.id;

  const hasSigned = effectiveDws.some(d => d.status === 'signed');
  const hasAgreed = effectiveVar && (effectiveVar.status === 'agreed' || effectiveVar.status === 'part_agreed');
  const hasDisputed = effectiveDws.some(d => d.status === 'disputed') || effectiveVar?.status === 'disputed' || ins.mc_response === 'disputed';
  const isDraft = ins.status === 'draft' && !ins.sent_at && !ins.mc_response;
  const rs = (hasSigned || !!hasAgreed) ? 'complete' : hasDisputed ? 'disputed' : isDraft ? 'draft' : 'inprogress';
  const rss = ROW_STATUS[rs];

  // ── Col 1: Instruction ──────────────────────────────────────────
  const col1 = (
    <div style={{ borderRadius: '6px', padding: '8px 10px', minHeight: '52px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ fontSize: '12px', fontWeight: '700', fontFamily: 'monospace', color: '#111', marginBottom: '3px' }}>{ins.reference}</div>
      <div style={{ fontSize: '11px', color: '#6b7280', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', lineHeight: '1.4' }}>
        {ins.description || '—'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '5px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.04em', ...typeBadge }}>{ins.type}</span>
        {ins.raised_by_mc && <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', textTransform: 'uppercase', background: '#e0f2fe', color: '#0369a1' }}>MC</span>}
        {ins.date && <span style={{ fontSize: '10px', color: '#9ca3af' }}>{formatDate(ins.date)}</span>}
        {!isEwnCvi && ins.linked_ewn_cvi_id && instrById?.[ins.linked_ewn_cvi_id] && (
          <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 6px', borderRadius: '3px', background: '#dbeafe', color: '#1e40af', letterSpacing: '0.02em' }}>
            ↑ {instrById[ins.linked_ewn_cvi_id].reference}
          </span>
        )}
      </div>
    </div>
  );

  // ── Col 2: IR / Instruction Link ────────────────────────────────
  let col2;
  if (!isEwnCvi) {
    col2 = <PipelineNode nodeData={{ state: 'empty', label: '—', sub: 'Direct instruction' }} onClick={null} />;
  } else if (linkedIr) {
    col2 = (
      <PipelineNode
        nodeData={{ state: 'complete', label: linkedIr.reference, sub: linkedIr.date ? formatDate(linkedIr.date) : '' }}
        onClick={e => { e.stopPropagation(); navigate('instruction-detail', { instructionId: linkedIr.id }); }}
      />
    );
  } else {
    const r = ins.mc_response;
    if (r === 'will_instruct') {
      col2 = (
        <PipelineNode nodeData={{ state: 'inprogress', label: 'MC confirmed instruction' }}>
          {ins.mc_expected_instruction_date && (() => {
            const expDate = new Date(ins.mc_expected_instruction_date + 'T00:00:00');
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const isPast = expDate < today;
            return (
              <div style={{ fontSize: '10px', color: isPast ? '#dc2626' : '#92400e', marginTop: '2px', fontWeight: '600' }}>
                Expected by: {expDate.toLocaleDateString('en-GB')}
              </div>
            );
          })()}
          <div style={{ fontSize: '10px', color: '#854F0B', marginTop: '2px' }}>Awaiting formal written instruction</div>
          <button onClick={e => { e.stopPropagation(); navigate('instruction-new', { type: 'IR', projectId, linkedEwnCviId: ins.id }); }}
            style={{ marginTop: '5px', background: '#111827', color: '#fff', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '10px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start' }}>
            + Record IR
          </button>
        </PipelineNode>
      );
    } else if (r === 'query') {
      const notes = (ins.mc_response_notes || '');
      col2 = <PipelineNode nodeData={{ state: 'inprogress', label: 'MC Query Raised', sub: notes.length > 60 ? notes.substring(0, 60) + '…' : notes }} onClick={null} />;
    } else if (r === 'disputed') {
      const notes = (ins.mc_response_notes || '');
      col2 = <PipelineNode nodeData={{ state: 'disputed', label: 'MC Disputed', sub: notes.length > 60 ? notes.substring(0, 60) + '…' : notes }} onClick={null} />;
    } else if (ins.sent_at) {
      col2 = <PipelineNode nodeData={{ state: 'empty', label: 'Awaiting MC Response', sub: `Sent ${formatDate(ins.sent_at)}` }} onClick={null} />;
    } else {
      col2 = (
        <PipelineNode nodeData={{ state: 'empty', label: 'Not yet sent to MC' }}>
          <button onClick={e => { e.stopPropagation(); navigate('instruction-detail', { instructionId: ins.id }); }}
            style={{ marginTop: '5px', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 8px', fontSize: '10px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start' }}>
            Send to MC →
          </button>
        </PipelineNode>
      );
    }
  }

  // ── Col 3: Valued As ────────────────────────────────────────────
  let col3;
  if (isEwnCvi && !linkedIr) {
    col3 = <PipelineNode nodeData={{ state: 'empty', label: '—', sub: 'Awaiting IR' }} onClick={null} />;
  } else if (effectiveDws.length === 0 && !effectiveVar) {
    col3 = (
      <PipelineNode nodeData={{ state: 'inprogress', label: '⚠ Not yet valued' }}>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '5px' }}>
          <button onClick={e => { e.stopPropagation(); navigate('daywork-new', { projectId, instructionId: effectiveInstrId }); }}
            style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '4px', padding: '3px 7px', fontSize: '10px', fontWeight: '600', cursor: 'pointer' }}>
            Record DW
          </button>
          <button onClick={e => { e.stopPropagation(); navigate('variation-new', { instructionId: effectiveInstrId, projectId }); }}
            style={{ background: 'transparent', color: '#633806', border: '1px solid #EF9F27', borderRadius: '4px', padding: '3px 7px', fontSize: '10px', fontWeight: '600', cursor: 'pointer' }}>
            Raise VAR
          </button>
        </div>
      </PipelineNode>
    );
  } else {
    col3 = (
      <div style={{ background: '#fff', border: `1px solid #e5e7eb`, borderRadius: '6px', padding: '8px 10px', height: '100%', minHeight: '52px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {effectiveDws.map(dw => (
          <div key={dw.id}
            onClick={e => { e.stopPropagation(); navigate('daywork-detail', { dayworkId: dw.id }); }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f0f9ff'; e.currentTarget.style.borderRadius = '4px'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.borderRadius = ''; }}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px', cursor: 'pointer', flexWrap: 'nowrap', padding: '1px 3px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', fontFamily: 'monospace', color: '#2563eb', textDecoration: 'underline', flexShrink: 0, whiteSpace: 'nowrap' }}>{dw.reference}</span>
            <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 5px', borderRadius: '8px', textTransform: 'uppercase', ...(DW_PILL[dw.status] || DW_PILL.draft), flexShrink: 0 }}>{dw.status}</span>
          </div>
        ))}
        {effectiveVar && (
          <div
            onClick={e => { e.stopPropagation(); navigate('variation-detail', { variationId: effectiveVar.id }); }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f0f9ff'; e.currentTarget.style.borderRadius = '4px'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.borderRadius = ''; }}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px', cursor: 'pointer', flexWrap: 'nowrap', padding: '1px 3px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', fontFamily: 'monospace', color: '#7c3aed', textDecoration: 'underline', flexShrink: 0, whiteSpace: 'nowrap' }}>{effectiveVar.reference}</span>
            <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 5px', borderRadius: '8px', textTransform: 'uppercase', ...(VAR_PILL[effectiveVar.status] || VAR_PILL.draft), flexShrink: 0 }}>{(effectiveVar.status || '').replace('_', ' ')}</span>
          </div>
        )}
      </div>
    );
  }

  // ── Col 4: Submitted to MC ──────────────────────────────────────
  const col4Lines = [];
  if (isEwnCvi && ins.sent_at) col4Lines.push({ text: `${ins.type} sent: ${formatDate(ins.sent_at)}`, muted: true });
  const submittedDw = effectiveDws.find(d => ['submitted', 'signed', 'disputed'].includes(d.status));
  if (submittedDw && submittedDw.submitted_at) col4Lines.push({ text: `DW: ${formatDate(submittedDw.submitted_at)}`, muted: false });
  else if (effectiveDws.length > 0 && !submittedDw) col4Lines.push({ text: 'DW: Not submitted', muted: true });
  if (effectiveVar) {
    if (effectiveVar.status === 'qs_review') {
      col4Lines.push({ text: `VAR: With QS${effectiveVar.raised_at ? ` — ${formatDate(effectiveVar.raised_at)}` : ''}`, muted: false });
    } else if (effectiveVar.submitted_at) {
      col4Lines.push({ text: `VAR: ${formatDate(effectiveVar.submitted_at)}`, muted: false });
    } else {
      col4Lines.push({ text: 'VAR: Not submitted', muted: true });
    }
  }
  const col4HasDate = col4Lines.some(l => !l.muted);
  const col4 = col4Lines.length === 0
    ? <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />
    : (
      <div style={{ background: col4HasDate ? NODE_STYLE.inprogress.bg : 'transparent', border: col4HasDate ? NODE_STYLE.inprogress.border : '1px dashed #d1d5db', borderRadius: '6px', padding: '8px 10px', height: '100%', minHeight: '52px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {col4Lines.map((l, i) => (
          <div key={i} style={{ fontSize: '11px', lineHeight: '1.5', color: l.muted ? '#9ca3af' : NODE_STYLE.inprogress.color, fontWeight: l.muted ? '400' : '600' }}>{l.text}</div>
        ))}
      </div>
    );

  // ── Col 5: MC Response (commercial) ────────────────────────────
  let col5;
  const signedDw = effectiveDws.find(d => d.status === 'signed');
  const disputedDw = effectiveDws.find(d => d.status === 'disputed');
  const pendingDw = effectiveDws.find(d => d.status === 'submitted');
  if (effectiveDws.length === 0 && !effectiveVar) {
    col5 = <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />;
  } else if (effectiveDws.length > 0) {
    if (disputedDw) col5 = <PipelineNode nodeData={{ state: 'disputed', label: '✗ Disputed', sub: disputedDw.signed_at ? formatDate(disputedDw.signed_at) : '' }} onClick={null} />;
    else if (signedDw) col5 = <PipelineNode nodeData={{ state: 'complete', label: '✓ Signed', sub: signedDw.signed_at ? formatDate(signedDw.signed_at) : '' }} onClick={null} />;
    else if (pendingDw) col5 = <PipelineNode nodeData={{ state: 'empty', label: 'Awaiting signature' }} onClick={null} />;
    else col5 = <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />;
  } else if (effectiveVar) {
    const vs = effectiveVar.status;
    const respDate = effectiveVar.mc_response_at ? formatDate(effectiveVar.mc_response_at) : '';
    const valStr = effectiveVar.value != null ? formatCurrency(effectiveVar.value) : '';
    const datVal = [respDate, valStr].filter(Boolean).join(' · ');
    if (vs === 'agreed') col5 = <PipelineNode nodeData={{ state: 'complete', label: '✓ Agreed', sub: datVal }} onClick={null} />;
    else if (vs === 'part_agreed') col5 = <PipelineNode nodeData={{ state: 'inprogress', label: '~ Part Agreed', sub: datVal }} onClick={null} />;
    else if (vs === 'disputed') col5 = <PipelineNode nodeData={{ state: 'disputed', label: '✗ Disputed', sub: respDate }} onClick={null} />;
    else if (vs === 'qs_review') col5 = <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />;
    else if (vs === 'submitted' || vs === 'priced') col5 = <PipelineNode nodeData={{ state: 'empty', label: 'Awaiting response' }} onClick={null} />;
    else col5 = <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />;
  } else {
    col5 = <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />;
  }

  // ── Col 6: On Application ───────────────────────────────────────
  const col6 = effectiveVar?.payapp_number
    ? <PipelineNode nodeData={{ state: 'complete', label: `App #${effectiveVar.payapp_number}`, sub: effectiveVar.payapp_date ? formatDate(effectiveVar.payapp_date) : '' }} onClick={null} />
    : <PipelineNode nodeData={{ state: 'empty', label: '—' }} onClick={null} />;

  function handleRowClick() {
    if (effectiveDws.length > 0) {
      const dw = effectiveDws.find(d => d.status === 'signed')
              || effectiveDws.find(d => d.status === 'submitted')
              || effectiveDws[0];
      navigate('daywork-detail', { dayworkId: dw.id });
    } else if (effectiveVar) {
      navigate('variation-detail', { variationId: effectiveVar.id });
    } else {
      navigate('instruction-detail', { instructionId: navTarget });
    }
  }

  return (
    <div
      key={ins.id}
      onClick={handleRowClick}
      style={{ display: 'grid', gridTemplateColumns: PIPELINE_GRID, gap: '6px', alignItems: 'stretch', minHeight: '68px', cursor: 'pointer', background: rss.bg, borderLeft: `4px solid ${rss.borderColor}`, borderRadius: '6px', padding: '4px 4px 4px 0' }}
    >
      <div style={{ paddingLeft: '4px' }}>{col1}</div>
      <div>{col2}</div>
      <div>{col3}</div>
      <div>{col4}</div>
      <div>{col5}</div>
      <div>{col6}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '18px', userSelect: 'none' }}>›</div>
    </div>
  );
}

export function ProjectDetailPage({ navigate, params }) {
  const { profile } = useAuth();
  const projectId = params?.projectId;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [instructions, setInstructions] = useState([]);
  const [irByParent, setIrByParent] = useState({});
  const [instrById, setInstrById] = useState({});
  const [rates, setRates] = useState(null);

  const role = profile?.role || '';

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadPipeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // 8-second loading safety net
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function loadProject() {
    const { data } = await supabase.from('projects').select('*').eq('id', projectId).single();
    setProject(data);
    setLoading(false);
  }

  async function loadPipeline() {
    const [{ data, error: err }, ratesData] = await Promise.all([
      supabase
        .from('instructions')
        .select('id, reference, type, description, date, sent_at, mc_response, mc_response_at, mc_response_notes, mc_expected_instruction_date, mc_instruction_ref, raised_by_mc, linked_ewn_cvi_id, parent_instruction_id, dayworks(id, reference, status, submitted_at, signed_at, daywork_labour(trade, hours_calculated), daywork_materials(quantity, price_per_unit), daywork_plant(quantity, rate)), variations(id, reference, title, status, value, submitted_at, raised_at, mc_response, mc_response_at, payapp_number, created_at)')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true }),
      fetchLabourRates(projectId),
    ]);
    if (err) console.error('loadPipeline error:', err);
    const fetched = data || [];
    const lookup = {};
    const byId = {};
    fetched.forEach(ins => {
      byId[ins.id] = ins;
      if (ins.linked_ewn_cvi_id) lookup[ins.linked_ewn_cvi_id] = ins;
    });
    setIrByParent(lookup);
    setInstrById(byId);
    setInstructions(fetched);
    setRates(ratesData);
  }

  if (loading) return <div style={S.spinner}>Loading project...</div>;
  if (error) return <div style={{ padding: '20px' }}><div style={{ color: C.danger, marginBottom: '12px' }}>{error}</div><button onClick={() => window.location.reload()} style={{ ...S.btn, ...S.btnGhost }}>Refresh Page</button></div>;
  if (!project) return <div style={{ padding: '24px' }}><div style={S.alertError}>Project not found.</div></div>;

  const canRaiseInstruction = role === 'proline_sm' || role === 'admin';

  const allDws = instructions.flatMap(ins => ins.dayworks || []);
  const allVars = instructions.flatMap(ins => ins.variations || []);
  const instrCount = instructions.filter(ins => {
    if (ins.type === 'EWN' || ins.type === 'CVI') return true;
    if (ins.type === 'IR' && !ins.linked_ewn_cvi_id) return true;
    return false;
  }).length;
  const dwCount = allDws.filter(d => d.status !== 'draft').length;
  const varCount = allVars.filter(v => v.status !== 'draft').length;
  const claimedValue = rates
    ? allDws.reduce((s, dw) => s + calcDayworkSheetCost(dw.daywork_labour, dw.daywork_materials, dw.daywork_plant, rates), 0)
      + allVars.reduce((s, v) => s + (parseFloat(v.value) || 0), 0)
    : 0;

  const displayInstructions = instructions.filter(ins => {
    if (ins.type === 'EWN' || ins.type === 'CVI') return true;
    if (ins.type === 'IR' && !ins.linked_ewn_cvi_id) return true;
    return false;
  });

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      {/* Black header bar */}
      <div style={{ background: '#000', padding: '0 24px', display: 'flex', alignItems: 'center', gap: '20px', minHeight: '64px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
            {[project.main_contractor, project.start_date ? formatDate(project.start_date) : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexShrink: 0 }}>
          {[
            { label: 'Instructions', value: instrCount },
            { label: 'Dayworks', value: dwCount },
            { label: 'Variations', value: varCount },
            { label: 'Claimed', value: formatCurrency(claimedValue) },
          ].map(stat => (
            <div key={stat.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>{stat.value}</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
            </div>
          ))}
        </div>
        {canRaiseInstruction && (
          <button
            onClick={() => navigate('instruction-new', { projectId })}
            style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            + Raise Instruction
          </button>
        )}
      </div>

      {/* Pipeline */}
      <div style={{ padding: '20px 24px', overflowX: 'auto' }}>
        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: PIPELINE_GRID, gap: '6px', marginBottom: '8px', minWidth: '900px' }}>
          <div style={{ paddingLeft: '8px', fontSize: '10px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{PIPELINE_COLS[0]}</div>
          {PIPELINE_COLS.slice(1).map(h => (
            <div key={h} style={{ fontSize: '10px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
          ))}
          <div />
        </div>

        {displayInstructions.length === 0 ? (
          <div style={{ ...S.emptyState, background: '#fff', borderRadius: '8px', border: `1px solid ${C.border}` }}>
            <div style={S.emptyStateText}>No instructions yet</div>
            {canRaiseInstruction && <div style={S.emptyStateSubtext}>Raise the first instruction for this project.</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '900px' }}>
            {displayInstructions.map(ins => renderPipelineRow({ ins, irByParent, instrById, navigate, projectId }))}
          </div>
        )}

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '20px', padding: '12px 0', borderTop: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
          {[
            { bg: ROW_STATUS.complete.bg,   border: ROW_STATUS.complete.borderColor,   label: 'Complete / Signed' },
            { bg: ROW_STATUS.inprogress.bg, border: ROW_STATUS.inprogress.borderColor, label: 'In Progress' },
            { bg: ROW_STATUS.disputed.bg,   border: ROW_STATUS.disputed.borderColor,   label: 'Disputed' },
            { bg: ROW_STATUS.draft.bg,      border: ROW_STATUS.draft.borderColor,      label: 'Draft' },
          ].map(({ bg, border, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: bg, border: `1px solid ${border}`, borderLeft: `3px solid ${border}`, flexShrink: 0 }} />
              <span style={{ fontSize: '11px', color: C.textMuted }}>{label}</span>
            </div>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: C.textMuted }}>Click any row to open</span>
        </div>
      </div>
    </div>
  );
}

// ---- Instruction Dayworks List Page ----
export function InstructionDayworksPage({ navigate, params }) {
  const instructionRef = params?.instructionRef || 'Instruction';
  const instructionId = params?.instructionId;
  const projectId = params?.projectId;
  const [dayworks, setDayworks] = useState([]);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!instructionId) return;
    supabase
      .from('dayworks')
      .select('id, reference, date_of_works, status, project_id, daywork_labour(trade, hours_calculated, start_time, finish_time), daywork_materials(quantity, price_per_unit), daywork_plant(quantity, rate)')
      .eq('instruction_id', instructionId)
      .order('date_of_works', { ascending: true })
      .then(({ data }) => {
        setDayworks(data || []);
        const pid = projectId || (data && data.length > 0 ? data[0].project_id : null);
        if (pid) fetchLabourRates(pid).then(setRates).catch(() => {});
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionId]);

  const DW_STATUS = {
    draft:     { background: '#f3f4f6', color: '#6b7280' },
    submitted: { background: '#fef3c7', color: '#92400e' },
    signed:    { background: '#d1fae5', color: '#065f46' },
    disputed:  { background: '#fee2e2', color: '#991b1b' },
  };

  const backProjectId = projectId || (dayworks.length > 0 ? dayworks[0].project_id : null);

  return (
    <div style={S.pageContainer}>
      <button
        onClick={() => navigate('project-detail', { projectId: backProjectId })}
        style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '16px' }}
      >
        ← Back to Project
      </button>

      {/* Header panel */}
      <div style={{ background: '#000', borderRadius: '8px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{instructionRef}</div>
        <div style={{ fontSize: '18px', fontWeight: '500', color: '#fff' }}>Linked Dayworks</div>
      </div>

      {loading ? (
        <div style={S.spinner}>Loading...</div>
      ) : dayworks.length === 0 ? (
        <div style={{ ...S.emptyState, background: '#fff', borderRadius: '8px', border: `1px solid ${C.border}` }}>
          <div style={S.emptyStateText}>No dayworks found</div>
        </div>
      ) : (() => {
          const rows = dayworks.map(dw => ({
            dw,
            hours: (dw.daywork_labour || []).reduce((s, r) =>
              s + (r.hours_calculated || calcHours(r.start_time, r.finish_time) || 0), 0),
            value: rates ? calcDayworkSheetCost(dw.daywork_labour, dw.daywork_materials, dw.daywork_plant, rates) : null,
          }));
          const totalHours = rows.reduce((s, r) => s + r.hours, 0);
          const totalValue = rates ? rows.reduce((s, r) => s + (r.value || 0), 0) : null;
          return (
            <div style={S.tableWrapper}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Reference</th>
                    <th style={S.th}>Date</th>
                    <th style={S.th}>Status</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Hours</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ dw, hours, value }) => {
                    const badge = DW_STATUS[dw.status] || DW_STATUS.draft;
                    return (
                      <tr
                        key={dw.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate('daywork-detail', { dayworkId: dw.id })}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: '700', color: C.text }}>{dw.reference}</td>
                        <td style={S.td}>{formatDate(dw.date_of_works)}</td>
                        <td style={S.td}><span style={{ ...S.badge, ...badge }}>{dw.status}</span></td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: '600', color: hours > 0 ? C.text : C.textMuted }}>
                          {hours > 0 ? `${fmtHours(hours)}h` : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: '600', color: value > 0 ? C.success : C.textMuted }}>
                          {value !== null ? (value > 0 ? formatCurrency(value) : '—') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: `2px solid ${C.border}`, background: '#f9fafb' }}>
                    <td colSpan={3} style={{ ...S.td, fontWeight: '700', color: C.text }}>Total</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: C.text }}>
                      {totalHours > 0 ? `${fmtHours(totalHours)}h` : '—'}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: C.success }}>
                      {totalValue !== null ? formatCurrency(totalValue) : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
    </div>
  );
}

// ---- All Variations Page ----
export function AllVariationsPage({ navigate }) {
  const { profile } = useAuth();
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const role = profile?.role || '';
  const isAdmin = role === 'admin';
  const isProlineQs = role === 'proline_qs';
  const isMcQs = role === 'mc_qs';

  useEffect(() => {
    loadVariations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 8-second loading safety net
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function loadVariations() {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('variations')
        .select('*, projects(id, name, reference), instructions(id, reference, type)')
        .order('created_at', { ascending: false });

      if (isMcQs) {
        query = query.in('status', ['submitted', 'agreed', 'part_agreed', 'disputed']);
      } else if (!isAdmin && !isProlineQs) {
        query = query.eq('created_by', profile.id);
      }

      const { data, error: e } = await query;
      if (e) throw e;
      setVariations(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const statuses = ['all', 'draft', 'submitted', 'agreed', 'part_agreed', 'disputed'];
  const filtered = statusFilter === 'all' ? variations : variations.filter(v => v.status === statusFilter);
  const totalValue = filtered.reduce((sum, v) => sum + (v.value || 0), 0);

  return (
    <div style={S.pageContainer}>
      <div style={S.pageHeader}>
        <h1 style={S.pageTitle}>All Variations</h1>
      </div>

      {error && <div style={S.alertError}>{error}</div>}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {statuses.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              ...S.btn, ...S.btnSm,
              ...(statusFilter === s ? S.btnPrimary : S.btnOutline),
            }}
          >
            {s === 'all' ? `All (${variations.length})` : s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
        {filtered.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: '14px', fontWeight: '600', color: C.text }}>
            Total: {formatCurrency(totalValue)}
          </span>
        )}
      </div>

      {loading ? (
        <div style={S.spinner}>Loading variations...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '48px 24px', color: C.textMuted }}>
          No variations found.
        </div>
      ) : (
        <div style={S.tableWrapper}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Reference', 'Title', 'Project', 'Instruction', 'Status', 'Value', 'Created'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => {
                const st = getStatusBadgeStyle(v.status);
                return (
                  <tr
                    key={v.id}
                    onClick={() => navigate('variation-detail', { variationId: v.id })}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: '700', color: C.accent }}>
                      {v.reference}
                    </td>
                    <td style={{ ...S.td, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.title}
                    </td>
                    <td style={S.td}>
                      <span style={{ fontSize: '13px', color: C.textMuted }}>
                        {v.projects?.reference} — {v.projects?.name}
                      </span>
                    </td>
                    <td style={S.td}>
                      {v.instructions ? (
                        <span style={{
                          background: C.header, color: '#fff', padding: '2px 8px',
                          borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', fontWeight: '700',
                        }}>
                          {v.instructions.reference}
                        </span>
                      ) : (
                        <span style={{ color: C.textMuted, fontSize: '13px' }}>—</span>
                      )}
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.badge, ...st }}>{v.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td style={{ ...S.td, fontWeight: '600' }}>
                      {v.value ? formatCurrency(v.value) : '—'}
                    </td>
                    <td style={{ ...S.td, color: C.textMuted, fontSize: '13px' }}>
                      {formatDate(v.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
